import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CompanyService, Company } from '../../../services/company.service';
import { Subject, takeUntil, filter } from 'rxjs';
import { SaveButtonComponent } from '../../save-button/save-button.component';
import { LoggerService } from '../../../services/logger.service';
import { VoucherDesignsPanelComponent } from './components/voucher-designs-panel/voucher-designs-panel.component';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';

@Component({
  selector: 'app-voucher-design-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    SaveButtonComponent,
    VoucherDesignsPanelComponent,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
    HlmNativeSelectImports,
    HlmTypographyImports,
    HlmSwitchImports,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  templateUrl: './voucher-design-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
/** The voucher designs and the PDF settings that decide what every design must show. */
export class VoucherDesignSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private initialFormValues: any;
  loading = false;
  formChanged = false;
  company: Company | null = null;

  @ViewChild('pdfSaveButton') pdfSaveButton!: SaveButtonComponent;

  designForm: FormGroup;

  constructor(
    private companyService: CompanyService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.designForm = this.fb.group({
      require_qr_code_on_voucher: [true],
      require_expiry_on_voucher: [true],
      // '' = the legal minimum of the company's country, else a number of years
      voucher_validity_years: [''],
    });

    this.designForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkFormChanged());

    // Making the date optional raises the minimum (Italy: 10 years): a chosen validity
    // at or below the new minimum has no effect and would be refused, so it falls back
    // to the minimum. Making it required again keeps whatever is chosen.
    this.designForm
      .get('require_expiry_on_voucher')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((required: boolean) => {
        if (required) return;
        const years = Number(this.designForm.get('voucher_validity_years')?.value) || 0;
        if (years && years <= this.minValidityYears(false))
          this.designForm.patchValue({ voucher_validity_years: '' });
      });
  }

  ngOnInit() {
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((c) => c !== null),
      )
      .subscribe((company) => {
        this.company = company;
        this.populateForm(company);
      });
  }

  private checkFormChanged() {
    if (this.initialFormValues) {
      this.formChanged = !Object.keys(this.initialFormValues).every(
        (key) => this.initialFormValues[key] === this.designForm.get(key)?.value,
      );
    }
  }

  private populateForm(company: Company) {
    this.designForm.patchValue(
      {
        require_qr_code_on_voucher: company.require_qr_code_on_voucher ?? true,
        require_expiry_on_voucher: company.require_expiry_on_voucher ?? true,
        // A saved value at or below the legal minimum (e.g. after a country change
        // or a design without the date) has no effect, so it shows as the minimum
        voucher_validity_years:
          (company.voucher_validity_years ?? 0) > this.minValidityYears()
            ? String(company.voucher_validity_years)
            : '',
      },
      { emitEvent: false },
    );
    this.initialFormValues = this.designForm.value;
    this.formChanged = false;
  }

  onSubmitPdfSettings(saveButton: SaveButtonComponent) {
    if (!this.designForm.valid) return;
    this.loading = true;
    saveButton.hideMessages();
    const years = this.designForm.value.voucher_validity_years;
    this.companyService
      .updateCompanyDetails({
        ...this.designForm.value,
        voucher_validity_years: years ? Number(years) : null,
      })
      .subscribe({
        next: () => {
          this.loading = false;
          this.initialFormValues = this.designForm.value;
          this.formChanged = false;
          saveButton.showSuccessMessage();
        },
        error: (error) => {
          const errorMessage =
            error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
          this.logger.error('Error saving PDF settings:', error);
          this.loading = false;
          saveButton.showErrorMessage(errorMessage);
        },
      });
  }

  isInvalid(controlName: string): boolean {
    const control = this.designForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  /** Whether the active designs print the expiry date (the designer decides that now). */
  get datePrinted(): boolean {
    return this.company?.voucher_expiry_printed !== false;
  }

  /** The date is on every voucher: required on every design (the switch as it stands
   * in the form) and printed by the active designs. With the requirement off a design
   * without the date may be activated any time, so the date counts as absent. */
  get dateGuaranteed(): boolean {
    return !!this.designForm.get('require_expiry_on_voucher')?.value && this.datePrinted;
  }

  /** Shortest validity for the company's country, given whether the date is guaranteed. */
  minValidityYears(dateGuaranteed = this.dateGuaranteed): number {
    const min = dateGuaranteed
      ? this.company?.legal_min_validity_years_with_date
      : this.company?.legal_min_validity_years_without_date;
    return min ?? this.company?.legal_min_validity_years ?? 3; // Germany as fallback
  }

  get legalMinValidityYears(): number {
    return this.minValidityYears();
  }

  /** The date is not guaranteed, and that raises the minimum (Italy: 10 instead of 1 year). */
  get missingDateExtendsValidity(): boolean {
    return !this.dateGuaranteed && this.minValidityYears(false) > this.minValidityYears(true);
  }

  /** Longer validities on offer: above the minimum, at most 30 years (§ 202 BGB). */
  get validityYearOptions(): number[] {
    const options = new Set([2, 3, 5, 10, 15, 20, 25, 30]);
    // Keep a value set some other way (e.g. through the API) selectable
    const saved = this.company?.voucher_validity_years;
    if (saved) options.add(saved);
    return [...options].filter((y) => y > this.legalMinValidityYears).sort((a, b) => a - b);
  }

  yearsLabel(years: number): string {
    return years === 1 ? '1 Jahr' : `${years} Jahre`;
  }

  /** Validity new vouchers get with the current form value. */
  get effectiveValidityYears(): number {
    const years = Number(this.designForm.get('voucher_validity_years')?.value) || 0;
    return Math.max(years, this.legalMinValidityYears);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
